import { useState, useEffect } from 'react';
import { Camera, Calendar, ChevronLeft, ChevronRight, Search, Eye, Car, Download } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PHOTO_LABELS = {
  lateral_left: 'Latéral gauche',
  lateral_right: 'Latéral droit',
  front: 'Avant',
  back: 'Arrière',
  dashboard: 'Tableau de bord'
};

const AdminPhotos = ({ user, activeModule = "b2b" }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [photoData, setPhotoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotos, setSelectedPhotos] = useState(null);
  const [detailPhotos, setDetailPhotos] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchPlate, setSearchPlate] = useState('');

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchPhotoData();
  }, [selectedDate]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setEmployees(response.data.filter(e => e.role === 'employee'));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchPhotoData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/photos/admin/all-employees?date=${selectedDate}`, {
        withCredentials: true
      });
      setPhotoData(response.data);
    } catch (error) {
      console.error('Failed to fetch photo data:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewEmployeePhotos = async (userId, userName, date) => {
    setSelectedPhotos({ user_id: userId, name: userName, date });
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/photos/admin/employee/${userId}?date=${date}`, {
        withCredentials: true
      });
      setDetailPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      setDetailPhotos([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const filteredData = photoData.filter(item => {
    if (selectedEmployee !== 'all' && item.user.user_id !== selectedEmployee) return false;
    if (searchPlate && item.vehicle_plate && !item.vehicle_plate.toLowerCase().includes(searchPlate.toLowerCase())) return false;
    return true;
  });

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Photos des véhicules</h1>
          <p className="text-gray-500">Historique complet pour amendes et incidents</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Date navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => changeDate(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-auto"
                />
                <Button variant="outline" size="sm" onClick={() => changeDate(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Employee filter */}
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="w-full lg:w-48">
                  <SelectValue placeholder="Tous les salariés" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les salariés</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.user_id} value={emp.user_id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Plate search */}
              <div className="relative flex-1">
                <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par plaque..."
                  value={searchPlate}
                  onChange={(e) => setSearchPlate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date header */}
        <div className="text-center">
          <p className="text-lg font-semibold text-[#0F172A]">
            {new Date(selectedDate).toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </p>
        </div>

        {/* Photo list */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : filteredData.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Camera className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Aucune donnée pour cette date</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredData.map((item) => (
              <Card 
                key={item.user.user_id} 
                className={`cursor-pointer transition-colors ${
                  item.start_complete && item.end_complete ? 'hover:border-green-300' : 'hover:border-orange-300'
                }`}
                onClick={() => viewEmployeePhotos(item.user.user_id, item.user.name, selectedDate)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {item.user.picture ? (
                        <img src={item.user.picture} alt="" className="w-12 h-12 rounded-full" />
                      ) : (
                        <div className="w-12 h-12 bg-[#0F172A] rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-lg">{item.user.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-[#0F172A]">{item.user.name}</p>
                        <div className="flex gap-4 text-sm mt-1">
                          <span className={`flex items-center gap-1 ${item.start_complete ? 'text-green-600' : 'text-red-600'}`}>
                            {item.start_complete ? '✅' : '❌'} Début ({item.start_photos?.length || 0}/5)
                          </span>
                          <span className={`flex items-center gap-1 ${item.end_complete ? 'text-green-600' : 'text-red-600'}`}>
                            {item.end_complete ? '✅' : '❌'} Fin ({item.end_photos?.length || 0}/5)
                          </span>
                        </div>
                        {item.vehicle_plate && (
                          <p className="text-sm text-gray-500 font-mono mt-1">
                            <Car className="w-3 h-3 inline mr-1" />
                            {item.vehicle_plate}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost">
                      <Eye className="w-5 h-5 mr-2" /> Voir les photos
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Photo Detail Dialog */}
        <Dialog open={!!selectedPhotos} onOpenChange={() => setSelectedPhotos(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Photos de {selectedPhotos?.name}</span>
                <span className="text-sm font-normal text-gray-500">
                  {selectedPhotos?.date && new Date(selectedPhotos.date).toLocaleDateString('fr-FR')}
                </span>
              </DialogTitle>
            </DialogHeader>
            {loadingDetail ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A]"></div>
              </div>
            ) : detailPhotos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune photo pour cette date</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Start photos */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg text-[#0F172A] flex items-center gap-2">
                      <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                      Début de service
                    </h3>
                    {detailPhotos.find(p => p.session_type === 'start') && (
                      <span className="text-sm bg-gray-100 px-3 py-1 rounded-full font-mono">
                        {detailPhotos.find(p => p.session_type === 'start')?.vehicle_plate}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    {['lateral_left', 'lateral_right', 'front', 'back', 'dashboard'].map(type => {
                      const photo = detailPhotos.find(p => p.session_type === 'start' && p.photo_type === type);
                      return (
                        <div key={type} className="text-center">
                          {photo ? (
                            <div className="relative group">
                              <img 
                                src={photo.image_url || photo.image_base64} 
                                alt={type} 
                                className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90" 
                                onClick={() => window.open(photo.image_url || photo.image_base64, '_blank')} 
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-full aspect-square bg-red-50 border-2 border-dashed border-red-200 rounded-lg flex items-center justify-center">
                              <Camera className="w-8 h-8 text-red-300" />
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{PHOTO_LABELS[type]}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* End photos */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg text-[#0F172A] flex items-center gap-2">
                      <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                      Fin de service
                    </h3>
                    {detailPhotos.find(p => p.session_type === 'end') && (
                      <span className="text-sm bg-gray-100 px-3 py-1 rounded-full font-mono">
                        {detailPhotos.find(p => p.session_type === 'end')?.vehicle_plate}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    {['lateral_left', 'lateral_right', 'front', 'back', 'dashboard'].map(type => {
                      const photo = detailPhotos.find(p => p.session_type === 'end' && p.photo_type === type);
                      return (
                        <div key={type} className="text-center">
                          {photo ? (
                            <div className="relative group">
                              <img 
                                src={photo.image_url || photo.image_base64} 
                                alt={type} 
                                className="w-full aspect-square object-cover rounded-lg cursor-pointer hover:opacity-90" 
                                onClick={() => window.open(photo.image_url || photo.image_base64, '_blank')} 
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-full aspect-square bg-red-50 border-2 border-dashed border-red-200 rounded-lg flex items-center justify-center">
                              <Camera className="w-8 h-8 text-red-300" />
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{PHOTO_LABELS[type]}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminPhotos;
